import { Component, Injector, Input, OnInit } from '@angular/core';
import { NgGridItemConfig } from 'angular2-grid';
import { snakeCase } from 'lodash';
import { ApiClientService } from '../../../../shared/api-client.service';
import { MunicipalityCodeSelectionService } from '../../municipality-code-selection.service';
import { ModalService } from '../shared/modal/modal.service';
import { DashboardGridItemConfig } from './../../../views/dashboard/models/dashboard-grid-item-config.model';
import { DashboardVisualisationConfig } from './../../../views/dashboard/models/dashboard-visualisation-config';

@Component({
  selector: 'gz-new-widget-modal',
  templateUrl: './new-widget-modal.template.html',
  styleUrls: [
    '../../widgets/shared/widget/widget.style.scss',
    '../../../views/dashboard/chart-builder/chart-builder.style.scss',
    '../../../views/dashboard/olap/olap.style.scss',
    './new-widget-modal.style.scss'
  ]
})
export class NewWidgetModalComponent implements OnInit {
  @Input() handleSave: any;
  @Input() widgetConfiguration: DashboardGridItemConfig = null;

  data = null;

  title = 'Add new widget';

  isFetching = true;

  errorMessage: string;

  defaultGridItemConfig: NgGridItemConfig = {
    sizex: 1,
    sizey: 2,
    minRows: 1,
    dragHandle: '.drag-handle'
  };

  availableQueries = [];

  selectedQuery;

  constructor(
    private apiClientService: ApiClientService,
    private injector: Injector,
    private modalService: ModalService,
    private municipalityCodeSelectionService: MunicipalityCodeSelectionService
  ) {
    this.handleSave = this.injector.get('handleSave');
    this.widgetConfiguration = this.injector.get('widgetConfiguration');
  }

  ngOnInit() {
    this.fetchCustomQueries(!this.widgetConfiguration);
  }

  fetchCustomQueries(initial: boolean = false) {
    this.isFetching = true;
    let visualisationType = this.widgetConfiguration
      ? this.widgetConfiguration.type
      : 'metric';
    if (visualisationType === 'chart')
      visualisationType += '_' + snakeCase(this.widgetConfiguration.axisType);
    this.apiClientService
      .get(`/dashboard/custom_queries?type=${visualisationType}`)
      .subscribe(data => {
        this.availableQueries = [];
        for (const query of data.customQueries) {
          this.availableQueries.push(query);
        }
        if (this.availableQueries.length > 0) {
          if (initial) this.selectedQuery = this.availableQueries[0].id;
          if (!this.widgetConfiguration) this.resetConfiguration();
          else if (initial)
            (this.widgetConfiguration
              .visualisationConfigs as DashboardVisualisationConfig).queryId = this.selectedQuery;
          else if (
            (this.widgetConfiguration
              .visualisationConfigs as DashboardVisualisationConfig).queryId
          )
            this.selectedQuery = (this.widgetConfiguration
              .visualisationConfigs as DashboardVisualisationConfig).queryId;
          this.fetchQuery(this);
        } else {
          if (!this.widgetConfiguration) this.resetConfiguration();
          this.errorMessage = 'No matching queries found.';
          this.isFetching = false;
        }
      });
  }

  onSaveClicked(e) {
    this.handleSave(this.widgetConfiguration);
  }

  fetchQuery(e) {
    this.onGetDataClicked(e, this.selectedQuery).subscribe(
      data => {
        this.data = data.data;
        (this.widgetConfiguration
          .visualisationConfigs as DashboardVisualisationConfig).chart = null;
        (this.widgetConfiguration
          .visualisationConfigs as DashboardVisualisationConfig).queryId = this.selectedQuery;
      },
      error => {
        this.errorMessage =
          'An error occured while fetching data. Please check if this query is valid!';
        this.isFetching = false;
      },
      () => {
        this.errorMessage = null;
        this.isFetching = false;
      }
    );
  }

  getQueryDescription() {
    return this.availableQueries.filter(
      q => q.id === parseInt(this.selectedQuery, 10)
    )[0].description;
  }

  querySelectionChanged(e) {
    this.fetchQuery(e);
  }

  visualisationTypeSelectionChanged(e) {
    this.selectedQuery = null;
    this.resetConfiguration();
    this.widgetConfiguration.type = e;
    switch (e) {
      case 'metric':
        (this.widgetConfiguration
          .visualisationConfigs as DashboardVisualisationConfig).type =
          'metric';
        break;
      case 'chart':
        (this.widgetConfiguration
          .visualisationConfigs as DashboardVisualisationConfig).type = 'chart';
        this.widgetConfiguration.axisType = 'axis';
        break;
      case 'map':
        (this.widgetConfiguration
          .visualisationConfigs as DashboardVisualisationConfig).type = 'map';
        break;
      case 'table':
      default:
        (this.widgetConfiguration
          .visualisationConfigs as DashboardVisualisationConfig).type = 'table';
    }
    this.fetchCustomQueries(true);
  }

  visualisationAxisTypeSelectionChanged(e) {
    this.fetchCustomQueries(true);
  }

  resetConfiguration() {
    this.widgetConfiguration = {
      type: 'metric',
      visualisationConfigs: {
        title: '',
        type: 'metric',
        metrics: [],
        chart: null,
        map: null,
        queryId: null
      },
      gridItemConfig: { ...this.defaultGridItemConfig }
    };
  }

  onGetDataClicked(e, queryId: number) {
    this.isFetching = true;
    const payload = {
      queryId: queryId,
      municipalityCode: this.municipalityCodeSelectionService.getSelectedCode(),
      from: '2017-01-01',
      to: '2017-08-31'
    };
    return this.apiClientService.post('/dashboard/custom_query', payload);
  }

  removeSelectedQuery() {
    const confResponse = confirm(
      'Do you really want to remove this query? All widgets using this query will fail at loading data!'
    );
    if (confResponse)
      this.apiClientService
        .delete('/dashboard/remove_query?id=' + this.selectedQuery)
        .subscribe(data => {
          this.fetchCustomQueries();
        });
  }
}
